import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronRight, Upload, Shield, Building, CreditCard, Activity, HelpCircle, Lightbulb, X } from 'lucide-react';
import { DocumentUpload } from '../../components/tenant/DocumentUpload';
import { AIRiskScoring } from '../../components/tenant/AIRiskScoring';
import { useAuth } from '../../hooks/useAuth';
import { MOCK_TENANT } from '../../utils/mockData';
import { motion, AnimatePresence } from 'framer-motion';
const STEPS = [{
  id: 1,
  title: 'Profile',
  icon: Shield,
  aiTip: 'Complete your profile accurately. This information helps us verify your identity and assess your eligibility for monthly rent payments.'
}, {
  id: 2,
  title: 'Documents',
  icon: Upload,
  aiTip: "Upload clear, valid documents. We accept International Passport, NIN, or Driver's License. Ensure documents are not expired and photos are legible."
}, {
  id: 3,
  title: 'Income',
  icon: CreditCard,
  aiTip: 'Connect your salary account securely via Mono. This allows us to verify your income and set up automatic monthly deductions for hassle-free payments.'
}, {
  id: 4,
  title: 'Risk Analysis',
  icon: Activity,
  aiTip: 'Our AI analyzes your financial profile to calculate your risk score. A lower score means better eligibility and potentially better terms.'
}, {
  id: 5,
  title: 'Agreement',
  icon: Building,
  aiTip: 'Review the Rent Settlement Agreement carefully. This legally binding document outlines your payment terms, rights, and responsibilities.'
}];
export function TenantOnboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAIGuide, setShowAIGuide] = useState(true);
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const currentStepData = STEPS.find(s => s.id === currentStep);
  const handleNext = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      if (currentStep < 5) {
        setCurrentStep(prev => prev + 1);
        setShowAIGuide(true); // Show guide for next step
      } else {
        navigate('/tenant/dashboard');
      }
    }, 1500);
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-6" data-id="element-5121">
            <div className="flex items-start justify-between" data-id="element-5122">
              <div data-id="element-5123">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5124">
                  Personal Information
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-5125">
                  Tell us about yourself to get started
                </p>
              </div>
              <button onClick={() => setShowAIGuide(true)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Show AI Guide" data-id="element-5126">
                <HelpCircle className="h-5 w-5" data-id="element-5127" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-5128">
              <div className="sm:col-span-3" data-id="element-5129">
                <label className="block text-sm font-medium text-gray-700" data-id="element-5130">
                  Full Name
                </label>
                <input type="text" defaultValue={user?.name} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-5131" />
              </div>
              <div className="sm:col-span-3" data-id="element-5132">
                <label className="block text-sm font-medium text-gray-700" data-id="element-5133">
                  Phone Number
                </label>
                <input type="text" defaultValue={user?.phone} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-5134" />
              </div>
              <div className="sm:col-span-6" data-id="element-5135">
                <label className="block text-sm font-medium text-gray-700" data-id="element-5136">
                  Current Address
                </label>
                <input type="text" placeholder="Enter your current residential address" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-5137" />
              </div>
            </div>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-5138">
            <div className="flex items-start justify-between" data-id="element-5139">
              <div data-id="element-5140">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5141">
                  Upload Documents
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-5142">
                  Verify your identity with official documents
                </p>
              </div>
              <button onClick={() => setShowAIGuide(true)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Show AI Guide" data-id="element-5143">
                <HelpCircle className="h-5 w-5" data-id="element-5144" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-6" data-id="element-5145">
              <DocumentUpload title="Valid ID Card" description="International Passport, NIN, or Driver's License" acceptedTypes="image/*,.pdf" onUpload={async () => {}} isUploaded={true} data-id="element-5146" />
              <DocumentUpload title="Tenancy Agreement" description="Signed copy of your current lease agreement" acceptedTypes=".pdf" onUpload={async () => {}} data-id="element-5147" />
            </div>
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-5148">
            <div className="text-center mb-8" data-id="element-5149">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100" data-id="element-5150">
                <CreditCard className="h-6 w-6 text-blue-600" data-id="element-5151" />
              </div>
              <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-5152">
                Income Verification
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-5153">
                Connect your salary account to verify rent capacity.
              </p>
              <button onClick={() => setShowAIGuide(true)} className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center" data-id="element-5154">
                <HelpCircle className="h-4 w-4 mr-1" data-id="element-5155" />
                Why do we need this?
              </button>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-5156">
              <div className="flex items-center justify-between mb-4" data-id="element-5157">
                <span className="text-sm font-medium text-gray-700" data-id="element-5158">
                  Supported Banks
                </span>
                <span className="text-xs text-gray-500" data-id="element-5159">Secured by Mono</span>
              </div>
              <div className="grid grid-cols-3 gap-2" data-id="element-5160">
                {['GTBank', 'Zenith', 'Access', 'UBA', 'First Bank', 'Kuda'].map(bank => <button key={bank} className="p-2 text-xs border bg-white rounded hover:border-blue-500 hover:text-blue-600 transition-colors" data-id="element-5161">
                    {bank}
                  </button>)}
              </div>
            </div>
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-5162">
            <div className="text-center mb-6" data-id="element-5163">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-5164">
                AI Risk Analysis
              </h3>
              <p className="text-sm text-gray-500" data-id="element-5165">
                We've analyzed your profile to determine your eligibility.
              </p>
              <button onClick={() => setShowAIGuide(true)} className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center" data-id="element-5166">
                <HelpCircle className="h-4 w-4 mr-1" data-id="element-5167" />
                How is this calculated?
              </button>
            </div>
            {MOCK_TENANT.riskScore && <AIRiskScoring riskData={MOCK_TENANT.riskScore} data-id="element-5168" />}
          </div>;
      case 5:
        return <div className="space-y-6" data-id="element-5169">
            <div className="flex items-start justify-between" data-id="element-5170">
              <div data-id="element-5171">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5172">
                  Rent Settlement Agreement
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-5173">
                  Review and accept the terms
                </p>
              </div>
              <button onClick={() => setShowAIGuide(true)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Show AI Guide" data-id="element-5174">
                <HelpCircle className="h-5 w-5" data-id="element-5175" />
              </button>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 h-64 overflow-y-auto text-sm text-gray-600" data-id="element-5176">
              <p className="mb-4" data-id="element-5177">
                <strong data-id="element-5178">1. PARTIES</strong>
                <br data-id="element-5179" />
                This agreement is made between Renta Platforms Ltd and the
                Tenant...
              </p>
              <p className="mb-4" data-id="element-5180">
                <strong data-id="element-5181">2. PAYMENT TERMS</strong>
                <br data-id="element-5182" />
                The Tenant agrees to pay the monthly rent sum of ₦150,000...
              </p>
              <p className="mb-4" data-id="element-5183">
                <strong data-id="element-5184">3. DEFAULT</strong>
                <br data-id="element-5185" />
                In the event of default, Renta reserves the right to...
              </p>
              <p data-id="element-5186">
                <strong data-id="element-5187">4. TERMINATION</strong>
                <br data-id="element-5188" />
                This agreement may be terminated by either party with...
              </p>
            </div>
            <div className="flex items-center" data-id="element-5189">
              <input id="agree" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5190" />
              <label htmlFor="agree" className="ml-2 block text-sm text-gray-900" data-id="element-5191">
                I have read and agree to the Terms & Conditions
              </label>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative" data-id="element-5192">
      <div className="max-w-3xl mx-auto" data-id="element-5193">
        {/* AI Guide Overlay */}
        <AnimatePresence data-id="element-5194">
          {showAIGuide && currentStepData && <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: -20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: -20
        }} className="fixed top-4 right-4 z-50 w-80 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-2xl p-6 text-white" data-id="element-5195">
              <button onClick={() => setShowAIGuide(false)} className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors" data-id="element-5196">
                <X className="h-5 w-5" data-id="element-5197" />
              </button>

              <div className="flex items-center mb-4" data-id="element-5198">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3 backdrop-blur-sm" data-id="element-5199">
                  <Lightbulb className="h-5 w-5" data-id="element-5200" />
                </div>
                <div data-id="element-5201">
                  <h4 className="font-bold text-sm" data-id="element-5202">AI Guide</h4>
                  <p className="text-xs text-blue-100" data-id="element-5203">
                    Step {currentStep} of {STEPS.length}
                  </p>
                </div>
              </div>

              <p className="text-sm leading-relaxed text-blue-50" data-id="element-5204">
                {currentStepData.aiTip}
              </p>

              <button onClick={() => setShowAIGuide(false)} className="mt-4 w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors backdrop-blur-sm" data-id="element-5205">
                Got it!
              </button>
            </motion.div>}
        </AnimatePresence>

        {/* Progress Bar */}
        <nav aria-label="Progress" className="mb-12" data-id="element-5206">
          <ol role="list" className="flex items-center" data-id="element-5207">
            {STEPS.map((step, stepIdx) => <li key={step.title} className={`relative ${stepIdx !== STEPS.length - 1 ? 'pr-8 sm:pr-20' : ''}`} data-id="element-5208">
                <div className="flex items-center" data-id="element-5209">
                  <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${step.id < currentStep ? 'bg-blue-600 hover:bg-blue-900' : step.id === currentStep ? 'border-2 border-blue-600 bg-white' : 'border-2 border-gray-300 bg-white'}`} data-id="element-5210">
                    {step.id < currentStep ? <CheckCircle className="h-5 w-5 text-white" aria-hidden="true" data-id="element-5211" /> : <step.icon className={`h-4 w-4 ${step.id === currentStep ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-5212" />}
                  </div>
                  <span className={`ml-3 text-sm font-medium hidden sm:block ${step.id <= currentStep ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-5213">
                    {step.title}
                  </span>
                </div>
                {stepIdx !== STEPS.length - 1 && <div className={`absolute top-4 left-0 -ml-px mt-0.5 h-0.5 w-full ${step.id < currentStep ? 'bg-blue-600' : 'bg-gray-200'}`} style={{
              left: '2rem',
              width: 'calc(100% - 2rem)'
            }} data-id="element-5214" />}
              </li>)}
          </ol>
        </nav>

        {/* Content Card */}
        <motion.div key={currentStep} initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.3
      }} className="bg-white shadow rounded-lg overflow-hidden" data-id="element-5215">
          <div className="px-4 py-5 sm:p-6" data-id="element-5216">{renderStepContent()}</div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6 flex justify-between" data-id="element-5217">
            <button type="button" disabled={currentStep === 1} onClick={() => setCurrentStep(prev => prev - 1)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors" data-id="element-5218">
              Back
            </button>
            <button type="button" onClick={handleNext} disabled={isProcessing} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors" data-id="element-5219">
              {isProcessing ? <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" data-id="element-5220"></div>
                  Processing...
                </> : <>
                  {currentStep === 5 ? 'Complete Setup' : 'Continue'}
                  <ChevronRight className="ml-2 -mr-1 h-4 w-4" data-id="element-5221" />
                </>}
            </button>
          </div>
        </motion.div>

        {/* Help Text */}
        <div className="mt-6 text-center" data-id="element-5222">
          <p className="text-sm text-gray-500" data-id="element-5223">
            Need help?{' '}
            <button onClick={() => setShowAIGuide(true)} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-5224">
              Ask AI Guide
            </button>{' '}
            or{' '}
            <a href="/help" className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-5225">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>;
}